import React from 'react';
import './Skeleton.scss';

// Componente base de skeleton
const Skeleton = ({ 
  width = '100%', 
  height = '20px', 
  borderRadius = '4px',
  className = '',
  variant = 'rectangular',
  animation = 'wave'
}) => {
  const skeletonClass = `skeleton skeleton--${variant} skeleton--${animation} ${className}`;
  
  return (
    <div 
      className={skeletonClass}
      style={{
        width,
        height,
        borderRadius
      }}
    />
  );
};

// Componente para líneas de texto
export const SkeletonText = ({ 
  lines = 1, 
  width = ['100%'], 
  height = '14px',
  spacing = '8px',
  className = ''
}) => {
  const widths = Array.isArray(width) ? width : Array(lines).fill(width);
  
  return (
    <div className={`skeleton-text ${className}`}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          width={widths[index] || widths[0]}
          height={height}
          className={index < lines - 1 ? `mb-${spacing}` : ''}
        />
      ))}
    </div>
  );
};

// Componente para imágenes
export const SkeletonImage = ({ 
  width = '100%', 
  height = '200px', 
  borderRadius = '8px',
  className = ''
}) => (
  <Skeleton
    width={width}
    height={height}
    borderRadius={borderRadius}
    className={`skeleton-image ${className}`}
  />
);

// Componente para avatares
export const SkeletonAvatar = ({ 
  size = '40px',
  className = ''
}) => (
  <Skeleton
    width={size}
    height={size}
    borderRadius="50%"
    variant="circular"
    className={`skeleton-avatar ${className}`}
  />
);

// Componente para botones
export const SkeletonButton = ({ 
  width = '120px', 
  height = '36px',
  className = ''
}) => (
  <Skeleton
    width={width}
    height={height}
    borderRadius="6px"
    className={`skeleton-button ${className}`}
  />
);

// Componente genérico para cards
export const SkeletonCard = ({
  imageHeight = '180px',
  titleLines = 2,
  contentLines = 3,
  hasButton = false,
  layout = 'vertical', // 'vertical' | 'horizontal'
  className = '',
  spacing = 'medium' // 'small' | 'medium' | 'large'
}) => {
  const cardClass = `skeleton-card skeleton-card--${layout} skeleton-card--${spacing} ${className}`;
  
  if (layout === 'horizontal') {
    return (
      <div className={cardClass}>
        <div className="skeleton-card__image">
          <SkeletonImage height="100%" borderRadius="4px" />
        </div>
        <div className="skeleton-card__content">
          <div className="skeleton-card__header">
            <SkeletonText lines={titleLines} width={['85%', '60%']} height="14px" />
          </div>
          <div className="skeleton-card__body">
            <SkeletonText 
              lines={contentLines} 
              width={['100%', '90%', '70%']} 
              height="12px" 
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="skeleton-card__image">
        <SkeletonImage height={imageHeight} borderRadius="8px 8px 0 0" />
      </div>
      <div className="skeleton-card__content">
        <div className="skeleton-card__header">
          <SkeletonText lines={titleLines} width={['90%', '70%']} height="14px" />
        </div>
        <div className="skeleton-card__body">
          <SkeletonText 
            lines={contentLines} 
            width={['100%', '85%', '60%']} 
            height="12px" 
          />
        </div>
        {hasButton && (
          <div className="skeleton-card__footer">
            <SkeletonButton width="100%" />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente para listas
export const SkeletonList = ({
  items = 3,
  itemHeight = '60px',
  spacing = '16px',
  className = ''
}) => (
  <div className={`skeleton-list ${className}`}>
    {Array.from({ length: items }, (_, index) => (
      <div 
        key={index}
        className="skeleton-list__item"
        style={{ marginBottom: index < items - 1 ? spacing : 0 }}
      >
        <Skeleton height={itemHeight} />
      </div>
    ))}
  </div>
);

// Componente específico para cumpleaños (usando componentes base)
export const SkeletonBirthday = ({ className = '' }) => (
  <div className={`skeleton-birthday ${className}`}>
    <SkeletonAvatar size="60px" />
    <div className="skeleton-birthday__info">
      <SkeletonText lines={2} width={['70%', '50%']} height="14px" />
    </div>
  </div>
);

export default Skeleton; 